import { Injectable, Signal, computed, effect, signal } from '@angular/core';
import { Product } from '../models/vendor.model';
import { CartItem, CartQuantityResult, CartTotals, parseMaxQuantityPerOrder } from '../models/cart.model';

const STORAGE_KEY = 'cartco.cart';

@Injectable({ providedIn: 'root' })
export class CartService {
  private readonly itemsSignal = signal<CartItem[]>(this.readFromStorage());

  readonly items: Signal<CartItem[]> = this.itemsSignal.asReadonly();
  readonly itemCount = computed(() => this.itemsSignal().reduce((acc, item) => acc + item.quantity, 0));
  /** Subtotal uses variant price when item has variant, else item price. */
  readonly subtotal = computed(() =>
    this.itemsSignal().reduce((acc, item) => {
      const unitPrice = item.variant?.price != null ? item.variant.price : item.price;
      return acc + unitPrice * item.quantity;
    }, 0)
  );
  readonly totals = computed<CartTotals>(() => {
    const subtotal = this.subtotal();
    const fees = subtotal > 0 ? subtotal * 0.05 : 0; // 5% service fee placeholder
    const taxes = 0;
    const total = subtotal + fees + taxes;
    return {
      itemCount: this.itemCount(),
      subtotal,
      fees,
      taxes,
      total,
    };
  });

  constructor() {
    effect(() => {
      this.persist(this.itemsSignal());
    });
  }

  /** Total units of a product from one vendor across all variant lines. */
  getTotalProductQuantity(productId: string, vendorId: string): number {
    return this.itemsSignal()
      .filter((item) => item.id === productId && item.vendorId === vendorId)
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  /** Stamp max-per-order onto every cart line for this product (from fresh API data). */
  syncProductMaxQuantity(productId: string, vendorId: string, maxQuantityPerOrder: unknown): void {
    const max = parseMaxQuantityPerOrder(maxQuantityPerOrder);
    let changed = false;
    const items = this.itemsSignal().map((item) => {
      if (item.id !== productId || item.vendorId !== vendorId) {
        return item;
      }
      if (parseMaxQuantityPerOrder(item.maxQuantityPerOrder) === max) {
        return item;
      }
      changed = true;
      if (max == null) {
        const { maxQuantityPerOrder: _removed, ...rest } = item;
        return rest as CartItem;
      }
      return { ...item, maxQuantityPerOrder: max };
    });
    if (changed) {
      this.itemsSignal.set(items);
    }
  }

  /** Stamp max-per-order for all products in one cart update (avoids per-product signal churn). */
  syncAllProductMaxQuantities(products: Map<string, Product>): void {
    const maxByProductVendor = new Map<string, number | null>();
    for (const [productId, product] of products) {
      if (product.vendorId) {
        maxByProductVendor.set(
          `${productId}:${product.vendorId}`,
          parseMaxQuantityPerOrder(product.maxQuantityPerOrder),
        );
      }
    }
    if (maxByProductVendor.size === 0) {
      return;
    }

    let changed = false;
    const items = this.itemsSignal().map((item) => {
      const max = maxByProductVendor.get(`${item.id}:${item.vendorId}`);
      if (max === undefined) {
        return item;
      }
      if (parseMaxQuantityPerOrder(item.maxQuantityPerOrder) === max) {
        return item;
      }
      changed = true;
      if (max == null) {
        const { maxQuantityPerOrder: _removed, ...rest } = item;
        return rest as CartItem;
      }
      return { ...item, maxQuantityPerOrder: max };
    });
    if (changed) {
      this.itemsSignal.set(items);
    }
  }

  addItem(item: Omit<CartItem, 'quantity'>, quantity = 1): CartQuantityResult {
    const qtyToAdd = Math.max(1, Math.floor(Number(quantity)) || 1);
    const max = parseMaxQuantityPerOrder(item.maxQuantityPerOrder);
    const currentTotal = this.getTotalProductQuantity(item.id, item.vendorId);

    if (max != null && currentTotal + qtyToAdd > max) {
      return { ok: false, reason: 'max_quantity', max };
    }

    const items = [...this.itemsSignal()];
    const index = items.findIndex(
      (existing) =>
        existing.id === item.id &&
        existing.vendorId === item.vendorId &&
        ((existing.variantId == null && item.variantId == null) ||
          (existing.variantId != null && existing.variantId === item.variantId))
    );

    const payload = max != null ? { ...item, maxQuantityPerOrder: max } : item;

    if (index >= 0) {
      items[index] = { ...items[index], ...payload, quantity: items[index].quantity + qtyToAdd };
    } else {
      items.push({ ...payload, quantity: qtyToAdd });
    }

    this.itemsSignal.set(items);
    return { ok: true };
  }

  updateQuantity(
    productId: string,
    quantity: number,
    vendorId?: string,
    variantId?: string,
  ): CartQuantityResult {
    if (quantity <= 0) {
      this.removeItem(productId, vendorId, variantId);
      return { ok: true };
    }

    const item = this.findCartItem(productId, vendorId, variantId);
    if (!item) {
      return { ok: true };
    }

    const max = parseMaxQuantityPerOrder(item.maxQuantityPerOrder);
    const nextQty = Math.floor(Number(quantity)) || 1;
    if (max != null) {
      const otherLinesQty = this.getTotalProductQuantity(item.id, item.vendorId) - item.quantity;
      if (otherLinesQty + nextQty > max) {
        return { ok: false, reason: 'max_quantity', max };
      }
    }

    const items = this.itemsSignal().map((line) => {
      const match = this.matchCartItem(line, productId, vendorId, variantId);
      return match ? { ...line, quantity: nextQty } : line;
    });
    this.itemsSignal.set(items);
    return { ok: true };
  }

  increment(productId: string, vendorId?: string, variantId?: string): CartQuantityResult {
    const item = this.findCartItem(productId, vendorId, variantId);
    if (!item) return { ok: true };

    const max = parseMaxQuantityPerOrder(item.maxQuantityPerOrder);
    const total = this.getTotalProductQuantity(item.id, item.vendorId);
    if (max != null && total >= max) {
      return { ok: false, reason: 'max_quantity', max };
    }

    return this.updateQuantity(productId, item.quantity + 1, vendorId, variantId);
  }

  decrement(productId: string, vendorId?: string, variantId?: string): void {
    const qty = this.getQuantity(productId, vendorId, variantId);
    this.updateQuantity(productId, qty - 1, vendorId, variantId);
  }

  removeItem(productId: string, vendorId?: string, variantId?: string): void {
    if (variantId != null) {
      this.itemsSignal.set(
        this.itemsSignal().filter(
          (item) => !(item.id === productId && item.vendorId === vendorId && item.variantId === variantId)
        )
      );
    } else {
      const predicate = vendorId != null
        ? (item: CartItem) => !(item.id === productId && item.vendorId === vendorId)
        : (item: CartItem) => item.id !== productId;
      this.itemsSignal.set(this.itemsSignal().filter(predicate));
    }
  }

  /** Remove all items from a specific vendor. */
  removeItemsByVendor(vendorId: string): void {
    this.itemsSignal.set(this.itemsSignal().filter((item) => item.vendorId !== vendorId));
  }

  clear(): void {
    this.itemsSignal.set([]);
  }

  getQuantity(productId: string, vendorId?: string, variantId?: string): number {
    const item = this.findCartItem(productId, vendorId, variantId);
    return item?.quantity ?? 0;
  }

  /**
   * Update the stored price and variant price of a cart item without changing quantity.
   * Used to sync stale localStorage prices when the admin changes price markups.
   */
  updateItemPrice(
    productId: string,
    vendorId: string,
    newPrice: number,
    variantId?: string,
    newVariantPrice?: number,
  ): void {
    let changed = false;
    const items = this.itemsSignal().map((item) => {
      if (
        item.id !== productId ||
        item.vendorId !== vendorId ||
        (variantId !== undefined && item.variantId !== variantId)
      ) {
        return item;
      }
      const priceUnchanged = item.price === newPrice;
      const variantPriceUnchanged =
        newVariantPrice === undefined ||
        (item.variant?.price === newVariantPrice && item.variant != null);
      if (priceUnchanged && variantPriceUnchanged) {
        return item;
      }
      changed = true;
      return {
        ...item,
        price: newPrice,
        ...(newVariantPrice !== undefined && item.variant
          ? { variant: { ...item.variant, price: newVariantPrice } }
          : {}),
      };
    });
    if (changed) {
      this.itemsSignal.set(items);
    }
  }

  private findCartItem(productId: string, vendorId?: string, variantId?: string): CartItem | undefined {
    const items = this.itemsSignal();
    return items.find((item) => this.matchCartItem(item, productId, vendorId, variantId));
  }

  private matchCartItem(item: CartItem, productId: string, vendorId?: string, variantId?: string): boolean {
    if (item.id !== productId) return false;
    if (vendorId != null && item.vendorId !== vendorId) return false;
    if (variantId !== undefined) {
      return variantId == null ? item.variantId == null : item.variantId === variantId;
    }
    return true;
  }

  private readFromStorage(): CartItem[] {
    if (typeof window === 'undefined') {
      return [];
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as CartItem[];
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch {
      // ignore storage errors
    }
    return [];
  }

  private persist(items: CartItem[]): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore storage errors
    }
  }
}
